import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { formatPrice } from '../../utils/formatters';
import api from '../../services/api';
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue
} from '../../components/admin/AdminFiltersModal';
import { resolveDateRange } from '../../utils/dateRangePresets';
import ListLoadError from '../../components/admin/ListLoadError';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import styles from './AccountsReceivablePage.module.css';

// Card 53705: "A, B and C" rather than "A and B and C" when more than one
// filter's option source failed. Mirrors the same local const in
// WelcomeCallsPage/SupportTicketsPage -- deliberately NOT extracted to a shared
// util, which would widen this card's blast radius past its one declared file.
const describeFilterList = (labels) =>
  labels.length <= 1
    ? labels.join('')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

// Card 53705: a filter whose options failed to load is dropped ENTIRELY by
// AdminFiltersModal (its :263 predicate keeps a filter only when
// `(def.options?.length ?? 0) > 0`), so the absence needs a voice HERE, beside
// the Filters button. Deliberately a proportionate inline row and NOT the
// block-level <ListLoadError>: that component is for list REGIONS, and this
// page already uses it correctly for the receivables table below.
const optionsErrorStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.375rem 0.625rem',
  border: '0.5px solid var(--color-tan-400)',
  borderRadius: '8px',
  background: 'var(--color-tan-50)',
  color: 'var(--color-error)',
  fontSize: '0.75rem',
  fontWeight: 600
};

const optionsRetryStyle = {
  padding: '0.25rem 0.625rem',
  border: '0.5px solid var(--color-tan-400)',
  borderRadius: '6px',
  background: 'var(--color-cream)',
  color: 'var(--color-charcoal)',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'background 0.2s ease, border-color 0.2s ease'
};

// Canonical receivable statuses — mirrors the backend AR_STATUS enum;
// "All statuses" is the empty value (the modal select's placeholder).
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending_charge', label: 'Pending Charge' },
  { value: 'settled', label: 'Settled' },
  { value: 'voided', label: 'Voided' },
  { value: 'locked', label: 'Locked' }
];

// The record has two meaningful dates — the range filter applies to the
// selected one (server default is Created when no field is chosen).
const DATE_FIELD_OPTIONS = [
  { value: 'created', label: 'Created' },
  { value: 'termEnd', label: 'Term end' }
];

// URL query params owned by the Filters experience — the URL is the source
// of truth; ?member= is page-owned (alert-email deep link) and preserved.
const SELECT_FILTER_PARAMS = ['status', 'dateField', 'tier', 'customTier', 'agency'];

const readFiltersFromParams = (searchParams) => {
  const values = {};
  SELECT_FILTER_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) values[key] = value;
  });
  const preset = searchParams.get('preset');
  if (preset) {
    values.date = {
      preset: preset.toUpperCase(),
      start: searchParams.get('from') || '',
      end: searchParams.get('to') || ''
    };
  }
  return values;
};

const writeFiltersToParams = (values, currentParams) => {
  const params = new URLSearchParams();
  const member = currentParams.get('member');
  if (member) params.set('member', member);
  SELECT_FILTER_PARAMS.forEach((key) => {
    if (values[key]) params.set(key, values[key]);
  });
  const date = values.date;
  if (date?.preset) {
    // an incomplete custom range applies nothing — don't persist it
    const isComplete = date.preset !== 'CUSTOM' || (date.start && date.end);
    if (isComplete) {
      params.set('preset', date.preset);
      if (date.preset === 'CUSTOM') {
        params.set('from', date.start);
        params.set('to', date.end);
      }
    }
  }
  return params;
};

const EVENT_LABELS = {
  clause_signed: 'Clause signed',
  clause_extended: 'Term extended',
  monthly_decrement: 'Monthly payment applied',
  order_voided: 'Order contribution voided',
  locked_on_delivery: 'Locked on delivery',
  frozen_on_cancel: 'Frozen on membership cancel',
  manual_charge: 'Manual charge — settled'
};

// The api client surfaces the server's error CODE as error.message — map the
// charge codes to member-displayable text (mirrors the API's message field).
const CHARGE_ERRORS = {
  CHARGE_IN_PROGRESS: 'A charge for this record is already being processed',
  INVALID_STATUS: 'Only pending-charge records can be charged',
  ZERO_BALANCE: 'Record has no outstanding balance to charge',
  NO_PAYMENT_PROFILE: 'Member has no stored payment profile — this balance must be collected manually',
  PAYMENT_DECLINED: "Payment was declined — the member's card could not be charged",
  SETTLE_FAILED: 'The charge succeeded but the record could not be marked settled — do NOT retry; manual reconciliation required',
  CHARGE_UNRESOLVED: 'The payment provider did not confirm this charge — do NOT charge again or use another card until it is reconciled'
};

const statusLabel = (status) => (status || 'unknown').replace(/_/g, ' ');

const eventLabel = (type) => EVENT_LABELS[type]
  ?? `${(type || 'event').charAt(0).toUpperCase()}${(type || 'event').slice(1).replace(/_/g, ' ')}`;

const shortId = (id) => (id ? id.slice(0, 8) : '—');

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const AccountsReceivablePage = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const memberFilter = searchParams.get('member') || '';

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  // A failed load is NOT an empty ledger. Without this the catch below left `records` at its
  // initial [] and the page told an operator no member owed a balance. (card 53564)
  const [loadError, setLoadError] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [standardTiers, setStandardTiers] = useState([]);
  const [customTiers, setCustomTiers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  // Card 53705: which filter labels lost their options to a GENUINE failure.
  // A predicted 403 never lands here -- see loadFilterOptions.
  const [optionErrors, setOptionErrors] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [chargeTarget, setChargeTarget] = useState(null);
  const [charging, setCharging] = useState(false);

  // URL is the single source of truth for filter state
  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

  const loadRecords = useCallback(async (page = 1) => {
    setLoading(true);
    setLoadError(false);
    try {
      const values = readFiltersFromParams(searchParams);
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (values.status) params.set('status', values.status);
      if (searchParams.get('member')) params.set('member', searchParams.get('member'));
      if (values.tier) params.set('membershipTier', values.tier);
      if (values.customTier) params.set('customTierId', values.customTier);
      if (values.agency) params.set('agencyId', values.agency);
      const range = resolveDateRange(values.date);
      if (range) {
        params.set('dateFrom', range.from);
        params.set('dateTo', range.to);
        if (values.dateField) params.set('dateField', values.dateField);
      }
      const response = await api.get(`/admin/accounts-receivable?${params.toString()}`);
      setRecords(response.records || []);
      setPagination(response.pagination || null);
    } catch (error) {
      // Clear rather than keep the previous page: every call here is user-initiated (mount,
      // filter change, pagination), so stale rows would sit under a filter that never matched
      // them. A polled list would keep them instead.
      setLoadError(true);
      setRecords([]);
      setPagination(null);
      toast.error(error.message || 'Failed to load accounts receivable');
    } finally {
      setLoading(false);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Any filter/member change reloads from page 1 (pagination resets)
  useEffect(() => {
    loadRecords(1);
  }, [loadRecords]);

  // Card 53705. The old shape here was `Promise.allSettled` + `if (res.status
  // === 'fulfilled')`, which silently skipped every `rejected` leg. Because
  // allSettled NEVER rejects there was no `catch` anywhere in this function, so
  // the site was invisible to the whole #53546-#53596 sweep (an AST census over
  // catch blocks) -- and the failure had no empty-state string either, because
  // AdminFiltersModal DROPS a zero-option select outright. Net effect: three
  // labelled controls vanished and the page said nothing at all.
  //
  // A PREDICTED 403 is NOT a load failure: these endpoints are gated by other
  // permission modules, so a denial keeps the original fail-soft silence
  // (card 49621). Only a genuine failure earns a voice. `error.status` is set on
  // every api.js error (services/api.js:255), which is what separates the arms.
  const loadFilterOptions = useCallback(() => {
    setOptionErrors([]);
    const noteFailure = (label, error) => {
      if (error?.status === 403) return;
      setOptionErrors((prev) => (prev.includes(label) ? prev : [...prev, label]));
    };

    api.get('/admin/membership-tiers')
      .then((response) => setStandardTiers((response.tiers || []).filter((t) => t.isActive)))
      .catch((error) => {
        setStandardTiers([]);
        noteFailure('Membership tier', error);
      });

    api.get('/admin/custom-membership-tiers')
      .then((response) => setCustomTiers((response.tiers || []).filter((t) => t.isActive)))
      .catch((error) => {
        setCustomTiers([]);
        noteFailure('Custom tier', error);
      });

    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, so it is not sent at all.
    if (canViewAgencies(hasPermission)) {
      api.get('/admin/agencies')
        .then((response) => setAgencies(response.agencies || []))
        .catch((error) => {
          setAgencies([]);
          noteFailure('Entity / Agency', error);
        });
    } else {
      setAgencies([]);
    }
  }, [hasPermission]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const loadDetail = async (id) => {
    setDetails((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), loading: true } }));
    try {
      const response = await api.get(`/admin/accounts-receivable/${id}`);
      setDetails((prev) => ({
        ...prev,
        [id]: { loading: false, events: response.events || [], consent: response.consent || null }
      }));
    } catch (error) {
      // `error: true` is what stops the panel below asserting "No consent record linked" about
      // a record whose consent was never fetched. (card 53564)
      setDetails((prev) => ({ ...prev, [id]: { loading: false, error: true, events: [], consent: null } }));
      toast.error(error.message || 'Failed to load record detail');
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!details[id]) loadDetail(id);
  };

  const clearMemberFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('member');
    setSearchParams(next, { replace: true });
  };

  const filterDefs = useMemo(
    () => [
      { key: 'dateField', label: 'Date field', type: 'select', placeholder: 'Created (default)', options: DATE_FIELD_OPTIONS },
      { key: 'date', label: 'Date', type: 'dateRange' },
      { key: 'status', label: 'Status', type: 'select', placeholder: 'All statuses', options: STATUS_OPTIONS },
      {
        key: 'tier',
        label: 'Membership tier',
        type: 'select',
        placeholder: 'All tiers',
        options: standardTiers.map((tier) => ({ value: tier.name, label: tier.name }))
      },
      {
        key: 'customTier',
        label: 'Custom tier',
        type: 'select',
        placeholder: 'All custom tiers',
        options: customTiers.map((tier) => ({ value: tier.id, label: tier.name }))
      },
      {
        key: 'agency',
        label: 'Entity / Agency',
        type: 'select',
        placeholder: 'All agencies',
        options: agencies.map((agency) => ({ value: agency.id || agency._id, label: agency.name }))
      }
    ],
    [standardTiers, customTiers, agencies]
  );

  const activeFilterCount = countActiveFilters(filters);
  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues, searchParams));
    setFiltersOpen(false);
  };

  const handleRemoveChip = (chip) => {
    applyFilters(removeFilterValue(filters, chip));
  };

  const handleResetFilters = () => {
    applyFilters({});
  };

  const openAgreementPdf = async (pdfUrl) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to load PDF');
      const blob = await response.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Failed to load agreement PDF');
    }
  };

  const handleCharge = async () => {
    if (!chargeTarget || charging) return;
    const record = chargeTarget;
    setCharging(true);
    try {
      const response = await api.post(`/admin/accounts-receivable/${record.id}/charge`);
      // Keep the enriched fields (member, order numbers, agreement link) —
      // the charge response carries the raw settled record only.
      setRecords((prev) => prev.map((r) => (r.id === record.id
        ? { ...r, ...response.record, member: r.member, sourceOrders: r.sourceOrders, agreementPdfUrl: r.agreementPdfUrl }
        : r)));
      setDetails((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      if (expandedId === record.id) loadDetail(record.id);
      toast.success(`${formatPrice(response.amountCents)} charged — record settled`);
      setChargeTarget(null);
    } catch (error) {
      toast.error(CHARGE_ERRORS[error.message] || error.message || 'Charge failed');
      setChargeTarget(null);
    } finally {
      setCharging(false);
    }
  };

  const filteredMemberName = memberFilter
    ? (records.find((r) => r.member?.id === memberFilter)?.member?.name || shortId(memberFilter))
    : '';

  if (loading && records.length === 0 && !pagination) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Loading accounts receivable...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Accounts <span className={styles.accentText}>Receivable</span>
          </h1>
          <p className={styles.subtitle}>
            Subscription clause balances — signed commitments, monthly reductions, and manual collection
          </p>
        </div>
      </div>

      <div className={styles.filterBar}>
        <FiltersButton activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />

        {optionErrors.length > 0 && (
          <div style={optionsErrorStyle} role="alert">
            <span>
              {`${describeFilterList(optionErrors)} ${
                optionErrors.length > 1 ? 'filters' : 'filter'
              } could not be loaded`}
            </span>
            <button type="button" style={optionsRetryStyle} onClick={loadFilterOptions}>
              Retry
            </button>
          </div>
        )}

        {memberFilter && (
          <div className={styles.memberChip}>
            <span>Member: {filteredMemberName}</span>
            <button type="button" onClick={clearMemberFilter} title="Show all records">×</button>
          </div>
        )}

        {pagination && (
          <span className={styles.countLabel}>
            {pagination.total} record{pagination.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <FilterChips chips={filterChips} onRemove={handleRemoveChip} />

      <div className={styles.tableWrapper}>
        {loadError ? (
          <ListLoadError
            message="Could not load accounts receivable"
            onRetry={() => loadRecords(1)}
          />
        ) : records.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            {activeFilterCount > 0 || memberFilter ? (
              <>
                <h3>No records match the current filters</h3>
                <p>Try removing a filter, or Reset / Clear All in the Filters panel.</p>
              </>
            ) : (
              <>
                <h3>No accounts receivable records</h3>
                <p>Records appear here when members sign the subscription clause at checkout.</p>
              </>
            )}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Source Orders</th>
                <th>Term Ends</th>
                <th>Monthly</th>
                <th>Months Left</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Agreement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                const detail = details[record.id];
                const isExpanded = expandedId === record.id;
                return [
                  <tr key={record.id} className={isExpanded ? styles.expandedRow : ''}>
                    <td>
                      {record.member ? (
                        <div className={styles.memberCell}>
                          <Link to={`/admin/members/${record.member.id}`} className={styles.memberLink}>
                            {record.member.name}
                          </Link>
                          <span className={styles.memberEmail}>{record.member.email}</span>
                        </div>
                      ) : (
                        <span className={styles.muted}>Unknown member</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.ordersCell}>
                        {(record.sourceOrders || []).length === 0 ? (
                          <span className={styles.muted}>—</span>
                        ) : (
                          record.sourceOrders.map((order) => (
                            <span key={order.orderId} className={styles.orderChip}>
                              {order.orderNumber || shortId(order.orderId)}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td>{formatDate(record.termEndDate)}</td>
                    <td>{formatPrice(record.monthlyAmountCents)}</td>
                    <td>{record.monthsRemaining}</td>
                    <td>
                      <span className={styles.balanceCell}>{formatPrice(record.balanceCents)}</span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[`st_${record.status}`] || ''}`}>
                        {statusLabel(record.status)}
                      </span>
                    </td>
                    <td>
                      {record.agreementPdfUrl ? (
                        <button
                          type="button"
                          className={styles.agreementLink}
                          onClick={() => openAgreementPdf(record.agreementPdfUrl)}
                        >
                          View
                        </button>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.actionBtns}>
                        <button
                          type="button"
                          className={styles.detailsBtn}
                          onClick={() => toggleExpand(record.id)}
                        >
                          {isExpanded ? 'Hide' : 'Details'}
                        </button>
                        {record.status === 'pending_charge' && (
                          <button
                            type="button"
                            className={styles.chargeBtn}
                            onClick={() => setChargeTarget(record)}
                          >
                            Process Payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${record.id}-detail`} className={styles.detailRow}>
                      <td colSpan={9}>
                        {!detail || detail.loading ? (
                          <div className={styles.detailLoading}>Loading record detail...</div>
                        ) : (
                          <div className={styles.detailContent}>
                            <div className={styles.detailGrid}>
                              <div className={styles.detailCard}>
                                <h4>Record</h4>
                                <div className={styles.detailField}><span>Balance</span><strong>{formatPrice(record.balanceCents)}</strong></div>
                                <div className={styles.detailField}><span>Months remaining</span><strong>{record.monthsRemaining} × {formatPrice(record.monthlyAmountCents)}</strong></div>
                                <div className={styles.detailField}><span>Term ends</span><strong>{formatDate(record.termEndDate)}</strong></div>
                                {record.frozenAt && (
                                  <div className={styles.detailField}><span>Frozen</span><strong>{formatDateTime(record.frozenAt)} ({formatPrice(record.frozenBalanceCents)})</strong></div>
                                )}
                                <div className={styles.detailField}><span>Created</span><strong>{formatDateTime(record.createdAt)}</strong></div>
                              </div>

                              <div className={styles.detailCard}>
                                <h4>Signed Agreement</h4>
                                {detail.consent ? (
                                  <>
                                    <div className={styles.detailField}><span>Signed</span><strong>{formatDateTime(detail.consent.consentTimestamp)}</strong></div>
                                    <div className={styles.detailField}><span>Terms version</span><strong>{detail.consent.termsVersion || '—'}</strong></div>
                                    <div className={styles.detailField}><span>Membership tier</span><strong>{detail.consent.membershipTier || '—'}</strong></div>
                                    <div className={styles.detailField}>
                                      <span>Monthly rate</span>
                                      <strong>{detail.consent.membershipMonthlyRate !== null ? `${formatPrice(detail.consent.membershipMonthlyRate)}/mo` : '—'}</strong>
                                    </div>
                                    <div className={styles.detailField}><span>Supply</span><strong>{detail.consent.supplyLabel || '—'}{detail.consent.internalMonths ? ` (${detail.consent.internalMonths} mo)` : ''}</strong></div>
                                    <div className={styles.detailField}><span>Signature</span><strong>{detail.consent.adoptedName || '—'}{detail.consent.signatureMode ? ` (${detail.consent.signatureMode})` : ''}</strong></div>
                                    <div className={styles.detailField}><span>IP address</span><strong>{detail.consent.ipAddress || '—'}</strong></div>
                                    {detail.consent.pdfUrl && (
                                      <button
                                        type="button"
                                        className={styles.agreementLink}
                                        onClick={() => openAgreementPdf(detail.consent.pdfUrl)}
                                      >
                                        View signed agreement PDF
                                      </button>
                                    )}
                                  </>
                                ) : detail.error ? (
                                  <p className={styles.detailError} role="alert">
                                    Consent record could not be loaded — this is not the same as having none.
                                  </p>
                                ) : (
                                  <p className={styles.muted}>No consent record linked</p>
                                )}
                              </div>

                              <div className={styles.detailCard}>
                                <h4>Source Orders</h4>
                                {(record.sourceOrders || []).length === 0 ? (
                                  <p className={styles.muted}>No source orders</p>
                                ) : (
                                  record.sourceOrders.map((order) => (
                                    <div key={order.orderId} className={styles.sourceOrder}>
                                      <div className={styles.detailField}>
                                        <span>{order.orderNumber || shortId(order.orderId)}</span>
                                        <strong className={styles.contributionStatus}>{statusLabel(order.status)}</strong>
                                      </div>
                                      <div className={styles.sourceOrderMeta}>
                                        {order.termMonths} mo × {formatPrice(order.monthlyRateCents)} · signed {formatDate(order.signedAt)}
                                        {order.orderStatus ? ` · order ${statusLabel(order.orderStatus)}` : ''}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>

                            <div className={styles.timeline}>
                              <h4>Reduction History</h4>
                              {detail.error ? (
                                <p className={styles.detailError} role="alert">
                                  Reduction history could not be loaded — this is not the same as having none.{' '}
                                  <button type="button" className={styles.detailRetry} onClick={() => loadDetail(record.id)}>
                                    Try again
                                  </button>
                                </p>
                              ) : (detail.events || []).length === 0 ? (
                                <p className={styles.muted}>No events recorded</p>
                              ) : (
                                detail.events.map((event) => (
                                  <div key={event.id} className={styles.timelineItem}>
                                    <span className={styles.timelineDot} />
                                    <div className={styles.timelineBody}>
                                      <div className={styles.timelineHead}>
                                        <strong>{eventLabel(event.eventType)}</strong>
                                        <span>{formatDateTime(event.createdAt)}</span>
                                      </div>
                                      <div className={styles.timelineMeta}>
                                        {event.monthsDelta ? `${event.monthsDelta > 0 ? '+' : ''}${event.monthsDelta} mo → ` : ''}
                                        {event.monthsRemainingAfter} mo remaining · balance {formatPrice(event.balanceAfter)}
                                        {event.actor ? ` · by ${event.actor}` : ''}
                                        {event.gatewayTxnId ? ` · txn ${event.gatewayTxnId}` : ''}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                ];
              })}
            </tbody>
          </table>
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className={styles.paginationBar}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={!pagination.hasPrevPage}
            onClick={() => loadRecords(Math.max(pagination.page - 1, 1))}
          >
            Previous
          </button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={!pagination.hasNextPage}
            onClick={() => loadRecords(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {chargeTarget && (
        <div className={styles.modalOverlay} onClick={() => !charging && setChargeTarget(null)}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-label="Process payment confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={styles.modalTitle}>Process Payment</h3>
            <div className={styles.modalBody}>
              <div className={styles.modalRow}><span>Member</span><strong>{chargeTarget.member?.name || 'Unknown'}</strong></div>
              <div className={styles.modalRow}><span>Email</span><strong>{chargeTarget.member?.email || '—'}</strong></div>
              <div className={styles.modalRow}>
                <span>Amount</span>
                <strong className={styles.modalAmount}>{formatPrice(chargeTarget.balanceCents)}</strong>
              </div>
              <p className={styles.modalWarning}>
                This will charge the member&apos;s stored card for the outstanding clause balance.
                A receipt with the signed agreement will be emailed to the member. This cannot be undone.
              </p>
              {chargeTarget.member && !chargeTarget.member.hasStoredPaymentProfile && (
                <p className={styles.modalNoProfile}>
                  This member has no stored payment profile — the charge will be refused.
                </p>
              )}
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setChargeTarget(null)}
                disabled={charging}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmBtn}
                onClick={handleCharge}
                disabled={charging}
              >
                {charging ? 'Charging...' : `Charge ${formatPrice(chargeTarget.balanceCents)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={handleResetFilters}
      />
    </div>
  );
};

export default AccountsReceivablePage;
